import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { useSection, useSettings } from "../lib/useCMS";
import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/page/PageHero";
import { SEO } from "../components/page/SEO";

const FALLBACK = {
  meta_title: "Philosophy | Built Different Hockey",
  meta_description: "The Built Different Hockey philosophy: defensemen-specific development built on purpose, detail, and discipline.",
  hero_overline: "Our Philosophy",
  hero_heading: "Greatness is Earned",
  hero_subheading: "We don't run ice time. We teach hockey.",
  hero_image_url: "",
  intro_body: "",
  pillar_1_title: "", pillar_1_body: "",
  pillar_2_title: "", pillar_2_body: "",
  pillar_3_title: "", pillar_3_body: "",
  pillar_4_title: "", pillar_4_body: "",
  quote: "Outwork. Outthink. Outperform.",
  quote_attribution: "Built Different Hockey",
  cta_label: "Book a Clinic",
  cta_url: "",
};

export default function PhilosophyPage() {
  const p = useSection("page_philosophy", FALLBACK);
  const settings = useSettings({ square_booking_url: "" });
  const ctaUrl = p.cta_url || settings.square_booking_url;
  const pillars = [
    { t: p.pillar_1_title, b: p.pillar_1_body },
    { t: p.pillar_2_title, b: p.pillar_2_body },
    { t: p.pillar_3_title, b: p.pillar_3_body },
    { t: p.pillar_4_title, b: p.pillar_4_body },
  ].filter((x) => x.t || x.b);

  return (
    <PageShell>
      <SEO title={p.meta_title} description={p.meta_description} canonical="/philosophy" image={p.hero_image_url} />
      <PageHero overline={p.hero_overline} heading={p.hero_heading} subheading={p.hero_subheading} image={p.hero_image_url} />

      <section className="bg-[#0a0a0a] py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
          <p className="text-lg md:text-xl text-zinc-300 leading-relaxed whitespace-pre-line">{p.intro_body}</p>
        </div>
      </section>

      <section className="bg-[#0f0f0f] py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <p className="overline">The Pillars</p>
          <h2 className="font-display text-4xl md:text-5xl uppercase mt-4 mb-10 leading-tight">The four standards we coach by.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {pillars.map((pl, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.07 }} className="bento-card p-7 md:p-9">
                <div className="font-display text-5xl text-brand-light">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display uppercase text-2xl md:text-3xl mt-3 leading-tight">{pl.t}</h3>
                <p className="text-zinc-400 mt-3 leading-relaxed">{pl.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {p.quote && (
        <section className="bg-brand py-20 md:py-28 border-b border-white/5">
          <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16 text-center">
            <div className="font-display text-4xl md:text-6xl uppercase text-white leading-tight">{p.quote}</div>
            {p.quote_attribution && <div className="text-[10px] tracking-[0.3em] uppercase text-white/70 font-bold mt-6">{p.quote_attribution}</div>}
          </div>
        </section>
      )}

      <section className="bg-[#0a0a0a] py-20">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="overline">Ready</p>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-3 leading-tight">Train with intent.<br/>See the difference.</h2>
          </div>
          {ctaUrl && (
            <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
              {p.cta_label} <ArrowUpRight className="h-5 w-5" />
            </a>
          )}
        </div>
      </section>
    </PageShell>
  );
}
